import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { AuthGuard } from "./_guards/auth.guard";
import { configuration } from "../environments/environment";
import { PublicLayoutComponent } from "./pages/e-notification/layouts/public-layout/public-layout.component";
import { AuthLayoutComponent } from "./pages/e-notification/layouts/auth-layout/auth-layout.component";

const routes: Routes = [
  // Auth callback route - handles redirect from AWS Cognito
  {
    path: "auth-callback",
    loadComponent: () =>
      import("./components/auth-callback/auth-callback.component").then(
        (m) => m.AuthCallbackComponent
      ),
  },
  // Simple sign-in route - redirects to hosted UI via auth service
  {
    path: "sign-in",
    loadComponent: () =>
      import("./components/sign-in-redirect/sign-in-redirect.component").then(
        (m) => m.SignInRedirectComponent
      ),
  },
  // Simple logged-out route - shows signout page
  {
    path: "logged-out",
    loadComponent: () =>
      import("./pages/auth-signout/auth-signout.component").then(
        (m) => m.AuthSignoutComponent
      ),
  },
  // Platform selection route - for WIPO admins and public users
  {
    path: "platform-selection",
    loadComponent: () =>
      import(
        "./components/platform-selection/platform-selection.component"
      ).then((m) => m.PlatformSelectionComponent),
    canActivate: [AuthGuard],
  },
  {
    path: "cognito-sync",
    loadChildren: () =>
      import("./pages/cognito-sync/cognito-sync.module").then(
        (m) => m.CognitoSyncModule
      ),
    canActivate: [AuthGuard],
  },
  // Auth routes with office and language parameters
  {
    path: ":officeCode/:langCode/sign-in",
    loadComponent: () =>
      import("./components/sign-in-redirect/sign-in-redirect.component").then(
        (m) => m.SignInRedirectComponent
      ),
  },
  {
    path: ":officeCode/:langCode/logged-out",
    loadComponent: () =>
      import("./pages/auth-signout/auth-signout.component").then(
        (m) => m.AuthSignoutComponent
      ),
  },

  {
    path: ":officeCode/:langCode/signup",
    loadComponent: () =>
      import("./pages/signup/signup.component").then((m) => m.SignupComponent),
  },
  // Main application routes with office and language parameters
  {
    path: ":officeCode/:langCode/dashboard",
    loadChildren: () =>
      import("./pages/dashboard/dashboard.module").then(
        (m) => m.DashboardModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: "public/:langCode/trusted-documents",
    component: PublicLayoutComponent,
    loadChildren: () =>
      import(
        "./pages/e-notification/public-notifications/public-notifications.module"
      ).then((m) => m.PublicNotificationsModule),
  },
  {
    path: ":officeCode/:langCode/notifications",
    component: AuthLayoutComponent,
    loadChildren: () =>
      import(
        "./pages/e-notification/auth-notification/auth-notifications.module"
      ).then((m) => m.AuthNotificationsModule),
    canActivate: [AuthGuard],
  },
  {
    path: ":officeCode/:langCode/notfound",
    loadChildren: () =>
      import("./pages/page-notfound/page-notfound.module").then(
        (m) => m.PageNotfoundModule
      ),
  },
  {
    path: ":officeCode/:langCode/unauthorized",
    loadComponent: () =>
      import("./pages/unauthorized/unauthorized.component").then(
        (m) => m.UnauthorizedComponent
      ),
  },
  // Redirects for office/:langCode pattern
  {
    path: ":officeCode/:langCode",
    redirectTo: ":officeCode/:langCode/dashboard",
    pathMatch: "full",
  },
  // Specific instance handlers - asean instance
  {
    path: "asean",
    redirectTo: "asean/fr/dashboard",
    pathMatch: "full",
  },
  // General redirects for office pattern - set default language
  {
    path: ":officeCode",
    redirectTo: (route) => {
      const officeCode = route.params["officeCode"];
      const officeConfig =
        configuration[officeCode] || configuration["default"];
      const defaultLang = officeConfig?.defaultLanguage || "en";
      return `/${officeCode}/${defaultLang}/dashboard`;
    },
    pathMatch: "full",
  },
  // Default redirect - redirect to sign-in
  {
    path: "",
    redirectTo: "sign-in",
    pathMatch: "full",
  },
  // Catch all route - 404
  {
    path: "**",
    redirectTo: "default/en/notfound",
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      initialNavigation: "enabledBlocking",
      scrollPositionRestoration: "enabled",
      paramsInheritanceStrategy: "always",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
